<template>
  <div class="container-fluid p-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center mb-3">
          <button class="btn btn-outline-secondary me-3" @click="goBack">
            <i class="bi bi-arrow-left me-2"></i>Back
          </button>
          <h2 class="mb-0 fw-bold gradient-text">Profile Settings</h2>
        </div>
      </div>
    </div>

    <!-- Profile Content -->
    <div class="row">
      <div class="col-lg-4 mb-4">
        <!-- Profile Picture Card -->
        <div class="modern-card p-4 text-center">
          <div class="profile-avatar-large mb-3">
            {{ currentUser?.avatar || currentUser?.name?.charAt(0) || 'U' }}
          </div>
          <h5 class="fw-bold mb-1">{{ currentUser?.name || 'User' }}</h5>
          <p class="text-muted mb-3">{{ getRoleDisplayName() }}</p>
          <button class="btn btn-outline-primary btn-sm">
            <i class="bi bi-camera me-2"></i>Change Photo
          </button>
        </div>

        <!-- Quick Stats -->
        <div class="modern-card p-4 mt-3" v-if="currentUser?.role === 'student'">
          <h6 class="fw-bold mb-3">Quick Stats</h6>
          <div class="d-flex justify-content-between mb-2">
            <span class="text-muted">Tasks Completed</span>
            <span class="fw-bold">{{ stats.tasksCompleted }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span class="text-muted">Badges Earned</span>
            <span class="fw-bold">{{ stats.badgesEarned }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-muted">Stories Read</span>
            <span class="fw-bold">{{ stats.storiesRead }}</span>
          </div>
        </div>
      </div>

      <div class="col-lg-8">
        <!-- Personal Information -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">Personal Information</h5>
          <form @submit.prevent="saveProfile">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Full Name</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="profileForm.name"
                  required
                >
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Email</label>
                <input 
                  type="email" 
                  class="form-control" 
                  v-model="profileForm.email"
                  required
                >
              </div>
            </div>
            
            <div class="row" v-if="currentUser?.role === 'student'">
              <div class="col-md-6 mb-3">
                <label class="form-label">Grade Level</label>
                <select class="form-select" v-model="profileForm.gradeLevel">
                  <option value="">Select Grade</option>
                  <option value="Kindergarten">Kindergarten</option>
                  <option value="Grade 1">Grade 1</option>
                  <option value="Grade 2">Grade 2</option>
                  <option value="Grade 3">Grade 3</option>
                  <option value="Grade 4">Grade 4</option>
                  <option value="Grade 5">Grade 5</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Class Code</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="profileForm.classCode"
                  placeholder="Enter class code"
                >
              </div>
            </div>

            <div class="row" v-if="currentUser?.role === 'parent'">
              <div class="col-md-6 mb-3">
                <label class="form-label">Phone Number</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  v-model="profileForm.phone"
                >
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Linked Children</label>
                <select class="form-select" multiple v-model="profileForm.childIds">
                  <option v-for="c in linkedChildren" :key="c.id" :value="c.id">{{ c.full_name }}</option>
                </select>
                <small class="text-muted">Use Parent Dashboard to add/remove children. This list updates automatically.</small>
              </div>
            </div>

            <div class="row" v-if="currentUser?.role === 'teacher'">
              <div class="col-md-6 mb-3">
                <label class="form-label">School</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="profileForm.school"
                >
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Subject</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="profileForm.subject"
                >
              </div>
            </div>

            <div class="d-flex gap-2">
              <button type="submit" class="btn btn-primary">
                <i class="bi bi-check-lg me-2"></i>Save Changes
              </button>
              <button type="button" class="btn btn-outline-secondary" @click="resetForm">
                <i class="bi bi-arrow-clockwise me-2"></i>Reset
              </button>
            </div>
          </form>
        </div>

        <!-- Account Settings -->
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-4">Account Settings</h5>
          
          <!-- Change Password -->
          <div class="mb-4">
            <h6 class="mb-3">Change Password</h6>
            <form @submit.prevent="changePassword">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Current Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    v-model="passwordForm.current"
                  >
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">New Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    v-model="passwordForm.new"
                  >
                </div>
              </div>
              <button type="submit" class="btn btn-outline-primary btn-sm">
                <i class="bi bi-shield-lock me-2"></i>Update Password
              </button>
            </form>
          </div>

          <!-- Notifications -->
          <div class="mb-4">
            <h6 class="mb-3">Notifications</h6>
            <div class="form-check mb-2">
              <input 
                class="form-check-input" 
                type="checkbox" 
                v-model="notificationSettings.email"
                id="emailNotifications"
              >
              <label class="form-check-label" for="emailNotifications">
                Email Notifications
              </label>
            </div>
            <div class="form-check mb-2">
              <input 
                class="form-check-input" 
                type="checkbox" 
                v-model="notificationSettings.progress"
                id="progressNotifications"
              >
              <label class="form-check-label" for="progressNotifications">
                Progress Updates
              </label>
            </div>
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                v-model="notificationSettings.reminders"
                id="reminderNotifications"
              >
              <label class="form-check-label" for="reminderNotifications">
                Task Reminders
              </label>
            </div>
          </div>

          <!-- Danger Zone -->
          <div class="border-top pt-4">
            <h6 class="text-danger mb-3">Danger Zone</h6>
            <button class="btn btn-outline-danger btn-sm" @click="confirmDeleteAccount">
              <i class="bi bi-trash me-2"></i>Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserProfile',
  data() {
    return {
      currentUser: null,
      profileForm: {
        name: '',
        email: '',
        gradeLevel: '',
        classCode: '',
        phone: '',
        childIds: [],
        school: '',
        subject: ''
      },
      passwordForm: {
        current: '',
        new: ''
      },
      notificationSettings: {
        email: true,
        progress: true,
        reminders: false
      },
      linkedChildren: [],
      stats: {
        tasksCompleted: 0,
        badgesEarned: 0,
        storiesRead: 0
      }
    }
  },
  mounted() {
    this.loadUserData()
    this.loadUserStats()
  },
  methods: {
    async loadUserData() {
      try {
        const storedUser = localStorage.getItem('currentUser')
        if (storedUser) {
          this.currentUser = JSON.parse(storedUser)
        }
        const { getProfile } = await import('../services/userService.js')
        const profile = await getProfile()
        // Merge server profile into local currentUser for consistent header/avatar use
        this.currentUser = {
          ...(this.currentUser || {}),
          id: profile.id,
          email: profile.email,
          role: profile.role,
          name: profile.name || (this.currentUser && this.currentUser.name) || 'User',
        }
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser))

        // Populate form from server
        this.profileForm = {
          name: profile.name || '',
          email: profile.email || '',
          gradeLevel: profile.gradeLevel || '',
          classCode: profile.classCode || this.currentUser.classCode || '',
          phone: this.currentUser.phone || '',
          childIds: [],
          school: this.currentUser.school || '',
          subject: this.currentUser.subject || ''
        }
        // Load linked children for parents
        if ((this.currentUser?.role || profile.role) === 'parent') {
          try {
            const { getChildren } = await import('../services/parentService.js')
            const res = await getChildren(this.currentUser.id)
            if (res?.success) {
              this.linkedChildren = res.data || []
              this.profileForm.childIds = this.linkedChildren.map(c => c.id)
            }
          } catch (e) {
            // ignore
          }
        }
        // Load notification toggles if server returned them
        if (profile.notification_settings && typeof profile.notification_settings === 'object') {
          const ns = profile.notification_settings
          this.notificationSettings = {
            email: ns.email ?? this.notificationSettings.email,
            progress: ns.progress ?? this.notificationSettings.progress,
            reminders: ns.reminders ?? this.notificationSettings.reminders,
          }
        }
      } catch (e) {
        // Fallback to localStorage only
        const storedUser = localStorage.getItem('currentUser')
        if (storedUser) {
          this.currentUser = JSON.parse(storedUser)
          this.profileForm = {
            name: this.currentUser.name || '',
            email: this.currentUser.email || '',
            gradeLevel: this.currentUser.gradeLevel || '',
            classCode: this.currentUser.classCode || '',
            phone: this.currentUser.phone || '',
            childIds: this.linkedChildren.map(c => c.id),
            school: this.currentUser.school || '',
            subject: this.currentUser.subject || ''
          }
        }
      }
    },

    async loadUserStats() {
      if (this.currentUser?.role !== 'student') return
      try {
        const userId = this.currentUser.id
        const [taskSvc, badgeSvc, readingSvc] = await Promise.all([
          import('../services/taskService.js'),
          import('../services/badgeService.js'),
          import('../services/readingService.js')
        ])

        // Completed tasks count
        let completedTasks = 0
        try {
          const completed = await taskSvc.getCompletedTasks(userId)
          completedTasks = Array.isArray(completed) ? completed.length : (Array.isArray(completed?.data) ? completed.data.length : 0)
        } catch (e) {
          // ignore
        }

        // Badges earned count
        let badgesEarned = 0
        try {
          const badgesData = await badgeSvc.getUserBadges(userId)
          badgesEarned = Array.isArray(badgesData?.completed) ? badgesData.completed.length : 0
        } catch (e) {
          // ignore
        }

        // Stories read count
        let storiesRead = 0
        try {
          const reading = await readingSvc.getSuggestions(userId)
          // If backend provides a count, use it; otherwise fallback to array length if structure allows
          if (typeof reading?.completed === 'number') {
            storiesRead = reading.completed
          } else if (Array.isArray(reading)) {
            storiesRead = reading.length
          }
        } catch (e) {
          // ignore
        }

        this.stats = {
          tasksCompleted: completedTasks,
          badgesEarned: badgesEarned,
          storiesRead: storiesRead
        }
      } catch {
        // Leave defaults if any error
      }
    },

    async saveProfile() {
      try {
        const { updateProfile } = await import('../services/userService.js')
        await updateProfile({
          email: this.profileForm.email,
          name: this.profileForm.name,
          gradeLevel: this.profileForm.gradeLevel,
          classCode: this.profileForm.classCode,
          notification_settings: this.notificationSettings,
        })
        // Refresh local cache of currentUser
        const { getProfile } = await import('../services/userService.js')
        const profile = await getProfile()
        this.currentUser = { ...(this.currentUser || {}), name: profile.name, email: profile.email, classCode: profile.classCode }
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser))
        this.showMessage('Profile updated successfully!')
      } catch (e) {
        this.showMessage('Failed to update profile')
      }
    },

    async changePassword() {
      if (!this.passwordForm.current || !this.passwordForm.new) {
        this.showMessage('Please fill in all password fields')
        return
      }
      try {
        const { changePassword } = await import('../services/userService.js')
        await changePassword({ currentPassword: this.passwordForm.current, newPassword: this.passwordForm.new })
        this.passwordForm = { current: '', new: '' }
        this.showMessage('Password updated successfully!')
      } catch (e) {
        this.showMessage('Failed to update password')
      }
    },

    resetForm() {
      this.loadUserData()
    },

    async confirmDeleteAccount() {
      if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) return
      try {
        const { deleteAccount } = await import('../services/userService.js')
        await deleteAccount()
      } catch (e) {
        // continue; still clear local state
      }
      localStorage.removeItem('authToken')
      localStorage.removeItem('currentUser')
      this.$router.push('/')
    },

    goBack() {
      this.$router.go(-1)
    },

    getRoleDisplayName() {
      const roleMap = {
        student: 'Student',
        parent: 'Parent',
        teacher: 'Teacher'
      }
      return roleMap[this.currentUser?.role] || 'User'
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.profile-avatar-large {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--role-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0 auto;
}

.form-label {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: var(--role-primary);
  box-shadow: 0 0 0 0.2rem rgba(var(--role-primary), 0.25);
}

.btn-primary {
  background: var(--role-gradient);
  border: none;
}

.btn-primary:hover {
  background: var(--role-primary);
  transform: translateY(-1px);
}

.btn-outline-primary {
  border-color: var(--role-primary);
  color: var(--role-primary);
}

.btn-outline-primary:hover {
  background-color: var(--role-primary);
  border-color: var(--role-primary);
}
</style> 